import { z } from 'zod';
import { ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

/**
 * AI persona type — which surface the call came from. Drives prompt
 * templating + per-persona rate-limit windows so the customer-facing
 * AI can't drain the budget the operator dashboard needs.
 *
 * Per [[project-ai-everywhere]] every persona gets its own AI surfaces;
 * this enum is the discriminator that lets one Cloud Function serve
 * all of them.
 */
export const AiPersonaSchema = z.enum([
  'ask_kilat_operator', // operator dashboard assistant
  'ask_carwash_customer', // customer-pwa Ask-the-carwash bubble
  'promo_suggest', // operator: "draft me a Rainy Day promo"
  'broadcast_compose', // operator: AI-drafted marketing push
  'share_caption', // operator: Share kit "Rewrite with AI" on a social caption
  'pitch_compose', // agent: drafted WhatsApp pitch
  'recruiter_insight', // recruiter dashboard insight
  'super_admin_query', // super-admin asking platform questions
  'internal_automation', // back-office: ops digest, anomaly detection, etc.
]);
export type AiPersona = z.infer<typeof AiPersonaSchema>;

/**
 * `aiAudit/{eventId}` — append-only ledger of every AI call.
 * Super-admin readable for incident review; admin SDK write-only.
 * Captures cost + latency so we can spot runaway spending fast.
 */
export const AiAuditEventSchema = z.object({
  id: ShortIdSchema,
  persona: AiPersonaSchema,
  tenantId: z.string().optional(),
  agentId: z.string().optional(),
  callerUid: UidSchema,
  /** Stable hash of the prompt + context (enables cache lookup + grouping). */
  promptHash: z.string(),
  /** Provider id ("together", "groq", "noop") for cost attribution. */
  provider: z.string(),
  model: z.string(),
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
  /** Microcents (sen × 100) so we can sum across calls without float drift. */
  costMicrocents: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
  /** Whether this call hit the cache (cost=0 + zero token use). */
  cacheHit: z.boolean(),
  /** Error code if the call failed (rate_limit_hit, provider_error, etc.). */
  errorCode: z.string().optional(),
  createdAt: TimestampSchema,
});
export type AiAuditEvent = z.infer<typeof AiAuditEventSchema>;

/**
 * `aiCache/{hash}` — content-addressable cache for AI responses. Same
 * `(persona, prompt, context-hash)` → same answer until expiry. Cheapest
 * way to keep the bill flat: most queries (Ask-the-carwash FAQs, common
 * "draft me a promo" intents) repeat across tenants/customers.
 */
export const AiCacheEntrySchema = z.object({
  id: z.string(), // = promptHash, set explicitly
  persona: AiPersonaSchema,
  response: z.string(),
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
  provider: z.string(),
  model: z.string(),
  createdAt: TimestampSchema,
  /** Firestore TTL field — set the project's TTL policy to this. */
  expireAt: TimestampSchema,
});
export type AiCacheEntry = z.infer<typeof AiCacheEntrySchema>;
